import { useState, useEffect, useRef, useCallback } from 'react';
import {
    Container,
    Title,
    Select,
    TextInput,
    Button,
    Stack,
    Group,
    Text,
    Paper,
    Image,
    Badge,
    ActionIcon,
    SimpleGrid,
    Box,
    Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconUpload, IconX, IconCopy, IconPhoto } from '@tabler/icons-react';
import { sitesApi, mediaApi } from '../api/client';

interface Site {
    id: number;
    site_name: string;
    url: string;
    is_active: boolean;
}

interface FileEntry {
    file: File;
    alt_text: string;
    title: string;
}

interface UploadedMedia {
    id: number;
    url: string;
    title: string;
    alt_text: string;
    mime_type: string;
    file_name: string;
}

export default function Media() {
    const [sites, setSites]         = useState<Site[]>([]);
    const [siteName, setSiteName]   = useState<string | null>(null);
    const [entries, setEntries]     = useState<FileEntry[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploaded, setUploaded]   = useState<UploadedMedia[]>([]);
    const [dragging, setDragging]   = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        sitesApi
            .getAll({ status: 'active', pageSize: 100 })
            .then((result) => setSites(Array.isArray(result.data) ? (result.data as unknown as Site[]) : []))
            .catch(() => notifications.show({ message: 'Failed to load sites', color: 'red' }));
    }, []);

    const addFiles = useCallback((incoming: FileList | null) => {
        if (!incoming) return;
        const images = Array.from(incoming).filter((f) => f.type.startsWith('image/'));
        if (images.length < incoming.length) {
            notifications.show({ message: 'Seuls les fichiers image sont acceptés', color: 'orange' });
        }
        setEntries((prev) => [
            ...prev,
            ...images.map((file) => ({ file, alt_text: '', title: '' })),
        ]);
    }, []);

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const removeFile = (index: number) =>
        setEntries((prev) => prev.filter((_, i) => i !== index));

    const updateEntry = (index: number, field: 'alt_text' | 'title', value: string) =>
        setEntries((prev) =>
            prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry))
        );

    const handleUpload = async () => {
        if (!siteName) {
            notifications.show({ message: 'Sélectionne un site', color: 'red' });
            return;
        }
        if (entries.length === 0) {
            notifications.show({ message: 'Ajoute au moins une image', color: 'red' });
            return;
        }

        setUploading(true);
        const results: UploadedMedia[] = [];

        for (const entry of entries) {
            try {
                const media = await mediaApi.upload({
                    file: entry.file,
                    site_name: siteName,
                    alt_text: entry.alt_text || undefined,
                    title: entry.title || undefined,
                });
                results.push({ ...media, file_name: entry.file.name });
            } catch {
                notifications.show({ message: `Échec pour ${entry.file.name}`, color: 'red' });
            }
        }

        setUploaded((prev) => [...results, ...prev]);
        setEntries([]);
        setUploading(false);

        if (results.length > 0) {
            notifications.show({
                message: `${results.length} image(s) uploadée(s) avec succès`,
                color: 'green',
            });
        }
    };

    const copyUrl = (url: string) => {
        navigator.clipboard.writeText(url);
        notifications.show({ message: 'URL copiée', color: 'blue' });
    };

    return (
        <Container size="xl">
            <Title order={1} mb="xl">
                Media Upload
            </Title>

            <Stack gap="md">
                <Select
                    label="Site WordPress"
                    placeholder="Choisir ou rechercher un site…"
                    data={sites
                        .filter((s) => s.is_active)
                        .map((s) => ({ value: s.site_name, label: s.site_name }))}
                    value={siteName}
                    onChange={setSiteName}
                    searchable
                    nothingFoundMessage="Aucun site trouvé"
                    required
                />

                {/* Drag & Drop Zone */}
                <Box
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={handleDrop}
                    style={{
                        border: `2px dashed ${dragging ? '#228be6' : '#ced4da'}`,
                        borderRadius: 8,
                        padding: 32,
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: dragging ? '#e7f5ff' : 'transparent',
                        transition: 'all 0.15s',
                    }}
                >
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => addFiles(e.target.files)}
                    />
                    <IconPhoto size={40} color="#adb5bd" style={{ marginBottom: 8 }} />
                    <Text fw={500}>Glisse des images ici ou clique pour choisir</Text>
                    <Text size="sm" c="dimmed">
                        PNG, JPG, WEBP, SVG — max 50 MB par fichier
                    </Text>
                </Box>

                {/* File list with per-file metadata */}
                {entries.length > 0 && (
                    <Stack gap="sm">
                        {entries.map((entry, i) => (
                            <Paper key={i} p="sm" withBorder>
                                <Group justify="space-between" mb="xs">
                                    <Text size="sm" fw={500}>
                                        {entry.file.name}{' '}
                                        <Text span c="dimmed" size="xs">
                                            ({(entry.file.size / 1024).toFixed(0)} KB)
                                        </Text>
                                    </Text>
                                    <Tooltip label="Remove file">
                                        <ActionIcon variant="subtle" color="red" onClick={() => removeFile(i)} aria-label="Remove file">
                                            <IconX size={14} />
                                        </ActionIcon>
                                    </Tooltip>
                                </Group>
                                <Group grow>
                                    <TextInput
                                        size="xs"
                                        placeholder="Alt text (description)"
                                        value={entry.alt_text}
                                        onChange={(e) => updateEntry(i, 'alt_text', e.currentTarget.value)}
                                    />
                                    <TextInput
                                        size="xs"
                                        placeholder="Titre (optionnel)"
                                        value={entry.title}
                                        onChange={(e) => updateEntry(i, 'title', e.currentTarget.value)}
                                    />
                                </Group>
                            </Paper>
                        ))}
                    </Stack>
                )}

                <Button
                    leftSection={<IconUpload size={16} />}
                    onClick={handleUpload}
                    loading={uploading}
                    disabled={entries.length === 0 || !siteName}
                >
                    {uploading
                        ? 'Upload en cours…'
                        : `Uploader${entries.length > 0 ? ` ${entries.length} fichier(s)` : ''}`}
                </Button>

                {/* Results */}
                {uploaded.length > 0 && (
                    <>
                        <Title order={3} mt="md">
                            Images uploadées
                        </Title>
                        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                            {uploaded.map((m) => (
                                <Paper key={m.id} shadow="xs" p="sm" withBorder>
                                    <Image
                                        src={m.url}
                                        alt={m.alt_text}
                                        radius="sm"
                                        mb="xs"
                                        fit="contain"
                                        h={120}
                                    />
                                    <Text size="sm" fw={500} truncate>
                                        {m.file_name}
                                    </Text>
                                    <Badge size="xs" variant="light" mb={4}>
                                        {m.mime_type}
                                    </Badge>
                                    <Group gap={4} wrap="nowrap">
                                        <Text
                                            size="xs"
                                            c="dimmed"
                                            style={{
                                                flex: 1,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {m.url}
                                        </Text>
                                        <Tooltip label="Copy URL">
                                            <ActionIcon
                                                size="sm"
                                                variant="subtle"
                                                onClick={() => copyUrl(m.url)}
                                                aria-label="Copy URL"
                                            >
                                                <IconCopy size={12} />
                                            </ActionIcon>
                                        </Tooltip>
                                    </Group>
                                </Paper>
                            ))}
                        </SimpleGrid>
                    </>
                )}
            </Stack>
        </Container>
    );
}
